// Pure filter and swatch logic for the Searchspring components. No DOM and no
// imports, so scripts/searchspring/tests can load it in Node
// (pnpm run searchspring:test).

/** The Searchspring fields the theme filters on. */
export const FILTER_FIELDS = {
  AVAILABILITY: 'mfield_searchspring_availability',
  COLORS: 'mfield_searchspring_colors',
  COLOR_GROUPS: 'mfield_searchspring_color_groups',
  SIZES: 'mfield_searchspring_sizes',
  TYPE_COLOR_SIZES: 'mfield_searchspring_type_color_sizes',
  TYPE_GROUP_SIZES: 'mfield_searchspring_type_group_sizes',
};

/** Availability value → the type code in type_color_sizes and type_group_sizes. */
export const AVAILABILITY_TYPE_CODES = {
  'Made to Order': 'MTO',
  'Ready to Ship': 'ECOM',
};

/**
 * The filter part of a Searchspring request: a `filter.` param per picked
 * value, plus background filters that tie the picks together so "Ready to
 * Ship + Whisper Blue" means Whisper Blue in Ready to Ship. Colors go into
 * type_color_sizes and color groups into type_group_sizes, each combined with
 * the picked availability and sizes, `*` standing for "not picked". Two
 * fields combine with AND, so a group plus a color needs both.
 *
 * snippets/searchspring-prefetch.liquid repeats this inline; keep them in step
 * (scripts/searchspring/tests/prefetch-drift.test.mjs).
 * @param {Array<{field: string, values: string[]}>} appliedFilters
 * @returns {string} '' or a query fragment starting with '&'
 */
export function buildFilterQuery(appliedFilters) {
  if (appliedFilters.length === 0) return '';

  const filterString = appliedFilters
    .slice()
    .sort((a, b) => a.field.localeCompare(b.field))
    .map((filter) =>
      filter.values
        .slice()
        .sort()
        .map((value) => `filter.${filter.field}=${encodeURIComponent(value)}`)
        .join('&')
    )
    .join('&');

  /** @param {string} field */
  const picked = (field) => appliedFilters.find((filter) => filter.field === field)?.values;
  const types = (picked(FILTER_FIELDS.AVAILABILITY) || ['*']).map(
    (type) =>
      AVAILABILITY_TYPE_CODES[/** @type {keyof typeof AVAILABILITY_TYPE_CODES} */ (type)] || type
  );
  const sizes = picked(FILTER_FIELDS.SIZES) || ['*'];
  const colorCodes = (picked(FILTER_FIELDS.COLORS) || ['*']).map((color) => color.split(':')[0]);
  const groups = picked(FILTER_FIELDS.COLOR_GROUPS);

  const bgfilters = [
    ...backgroundFilters(FILTER_FIELDS.TYPE_COLOR_SIZES, types, colorCodes, sizes),
    ...(groups ? backgroundFilters(FILTER_FIELDS.TYPE_GROUP_SIZES, types, groups, sizes) : []),
  ];

  return `&${filterString}${bgfilters.length > 0 ? `&${bgfilters.join('&')}` : ''}`;
}

/**
 * Every type × middle × size combination as a `bgfilter.` param, skipping
 * the all-`*` one, which would match everything.
 * @param {string} field
 * @param {string[]} types
 * @param {string[]} middles - color codes or group names
 * @param {string[]} sizes
 * @returns {string[]}
 */
function backgroundFilters(field, types, middles, sizes) {
  /** @type {string[]} */
  const params = [];
  for (const type of types) {
    for (const middle of middles) {
      for (const size of sizes) {
        if (type === '*' && middle === '*' && size === '*') continue;
        params.push(`bgfilter.${field}=${encodeURIComponent(`${type}-${middle}-${size}`)}`);
      }
    }
  }
  return params;
}

/**
 * The sort part of a Searchspring request: `&sort.<field>=<direction>`, plus
 * `&sort.<tiebreaker>=asc` when a tiebreaker is given and differs from the
 * field. Collections pass `title`: the category endpoint drops a lone
 * `sort.variant_price=asc`, and a second key also makes ties stable across
 * pages. snippets/searchspring-prefetch.liquid repeats this for collections.
 * @param {{ field: string | null, direction: string | null }} sorting
 * @param {string} [tiebreaker]
 * @returns {string}
 */
export function buildSortQuery(sorting, tiebreaker) {
  if (!sorting.field || !sorting.direction) return '';
  const sortQuery = `&sort.${sorting.field}=${sorting.direction}`;
  return tiebreaker && sorting.field !== tiebreaker
    ? `${sortQuery}&sort.${tiebreaker}=asc`
    : sortQuery;
}

/** Facets whose list stays full after a pick (see withoutPicks). */
export const LIST_SOURCE_FIELDS = [FILTER_FIELDS.COLORS, FILTER_FIELDS.COLOR_GROUPS];

/**
 * The key list-source caches use: the request URL without the params a facet
 * list doesn't depend on — sort, page and page size — so a sort or page-size
 * change with a color picked reuses the list. A restricted collection without
 * virtual pagination builds its lists from one page of products under the
 * current sort, so those keys must keep sort and page size instead — see
 * listsDependOnPaging in searchspring-results-base.js, which callers check
 * before using this key.
 * @param {string} requestKey - an absolute Searchspring request URL
 * @returns {string}
 */
export function listSourceCacheKey(requestKey) {
  const url = new URL(requestKey);
  for (const name of [...url.searchParams.keys()]) {
    if (name.startsWith('sort.') || name === 'page' || name === 'resultsPerPage') {
      url.searchParams.delete(name);
    }
  }
  return url.toString();
}

/**
 * The filters a facet's list comes from: the applied filters without that
 * facet's own picks. Searchspring works out every facet after the background
 * filters, so the request with a Color pick lists only the colors of the
 * styles that have it. Background filters are built from these filters, so
 * they lose the picks too. Returns copies: the components change the applied
 * filters in place while requests are in flight.
 * @param {Array<{field: string, values: string[]}>} appliedFilters
 * @param {string} field
 * @returns {Array<{field: string, values: string[]}>}
 */
export function withoutPicks(appliedFilters, field) {
  return appliedFilters
    .filter((filter) => filter.field !== field)
    .map((filter) => ({ field: filter.field, values: [...filter.values] }));
}

/**
 * Swaps each list-source facet's values for its list source's, checking the
 * picked ones. A pick the list source doesn't have keeps its entry from the
 * main facet, so it can still be unchecked. A pick neither source has is
 * added as a bare checked entry.
 * @param {any[]} facets - the main response's facets, as getFilterData shapes them
 * @param {Map<string, any>} listFacets - field → that facet from its list source
 * @param {Array<{field: string, values: string[]}>} appliedFilters
 * @returns {any[]}
 */
export function mergeListFacets(facets, listFacets, appliedFilters) {
  if (listFacets.size === 0) return facets;

  const merged = facets.map((facet) => {
    const listFacet = listFacets.get(facet.field);
    return listFacet ? { ...facet, values: listValues(listFacet, facet, appliedFilters) } : facet;
  });
  for (const [field, listFacet] of listFacets) {
    if (!facets.some((facet) => facet.field === field)) {
      merged.push({ ...listFacet, values: listValues(listFacet, null, appliedFilters) });
    }
  }
  return merged;
}

/**
 * The list source's values, picks checked. A pick missing from the list
 * source is carried over from the main facet; a pick missing from both is
 * added as a bare `{ value, label: value, active: true }` entry, in pick
 * order (zero-result states return no facets, e.g. a discontinued color in a
 * shared URL, or two picks no style has both of).
 * @param {any} listFacet
 * @param {any | null} mainFacet
 * @param {Array<{field: string, values: string[]}>} appliedFilters
 * @returns {any[]}
 */
function listValues(listFacet, mainFacet, appliedFilters) {
  const picked = new Set(
    appliedFilters.find((filter) => filter.field === listFacet.field)?.values || []
  );
  const values = listFacet.values.map((/** @type {any} */ value) => ({
    ...value,
    active: picked.has(value.value),
  }));
  const listed = new Set(values.map((/** @type {any} */ value) => value.value));
  for (const value of mainFacet?.values || []) {
    if (picked.has(value.value) && !listed.has(value.value)) {
      values.push({ ...value, active: true });
      listed.add(value.value);
    }
  }
  for (const value of picked) {
    if (!listed.has(value)) values.push({ value, label: value, active: true });
  }
  return values;
}

/**
 * Color facet values in the admin's color order, the order PDP swatches use.
 * Colors the order doesn't list go last, keeping the order they came in
 * (count order on collection pages, see filterFacetsBySalesStatus).
 * @template {{ value: string }} T
 * @param {T[]} values - "CODE:#hex" facet values
 * @param {Map<string, number>} colorOrder - lowercase color code → position
 * @returns {T[]}
 */
export function sortByColorOrder(values, colorOrder) {
  const position = (/** @type {T} */ v) =>
    colorOrder.get(v.value.split(':')[0].toLowerCase()) ?? Number.MAX_SAFE_INTEGER;
  return values.slice().sort((a, b) => position(a) - position(b));
}

/**
 * The card colors the active color filters put first, in order: direct Color
 * picks, most recent first, then the colors of a picked group (or of the
 * search query) in the card's own order. With no availability picked a card
 * lists Ready to Ship colors before Made to Order ones, so group colors come
 * Ready to Ship first too.
 * @param {string[]} cardCodes - the card's color codes, in display order
 * @param {string[]} activeColorFilters - "CODE:#hex" for a direct pick, in
 *   pick order; a bare "CODE" for a color from a group or the query
 * @returns {string[]}
 */
export function promotedSwatchCodes(cardCodes, activeColorFilters) {
  const onCard = new Set(cardCodes);
  /** @type {string[]} */
  const promoted = [];
  for (const value of [...activeColorFilters].reverse()) {
    const code = value.split(':')[0];
    if (value.includes(':') && onCard.has(code) && !promoted.includes(code)) promoted.push(code);
  }
  const fromGroups = new Set(activeColorFilters.filter((value) => !value.includes(':')));
  for (const code of cardCodes) {
    if (fromGroups.has(code) && !promoted.includes(code)) promoted.push(code);
  }
  return promoted;
}
